import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  Play, 
  CheckCircle, 
  Sparkles, 
  TrendingUp,
  Users,
  Shield,
  Zap,
  ChevronLeft,
  ChevronRight,
  Star
} from 'lucide-react';

const HeroSection = ({ handleNavigation }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const slides = [
    {
      id: 1,
      image: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564660/Leonardo_Kino_XL_create_an_image_where_a_hardworking_black_man_1_piqe0f.jpg",
      title: "A PLATAFORMA PARA O",
      highlight: "MEI FAZER MAIS",
      subtitle: "Da abertura até a gestão, impulsionamos o seu potencial empreendedor.",
    },
    {
      id: 2,
      image: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564662/Leonardo_Kino_XL_create_an_image_where_a_white_working_woman_i_1_qz8utx.jpg",
      title: "SIMPLIFIQUE SUA",
      highlight: "VIDA DE MEI",
      subtitle: "Gerencie DAS, emita notas fiscais e declare seu imposto em um só lugar, não tenha medo de multas.",
    },
    {
      id: 3,
      image: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564438/Leonardo_Kino_XL_create_an_image_where_an_older_white_man_is_l_0_tmiu6i.jpg",
      title: "CRESÇA COM",
      highlight: "CONFIANÇA",
      subtitle: "Mais de 500mil de MEIs nascem todos os Dias.",
    },
    {
      id: 4,
      image: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564659/Leonardo_Kino_XL_create_an_image_where_a_hardworking_black_man_2_jgrd2t.jpg",
      title: "SEU NEGÓCIO",
      highlight: "MERECE O MELHOR",
      subtitle: "Ferramentas profissionais para microempreendedores de verdade.",
    },
  ];

  const benefits = [
    { icon: Zap, text: '100% Online' },
    { icon: Shield, text: 'Seguro' },
    { icon: TrendingUp, text: '7 dias Gratuitos' },
  ];

  // Auto-play slides
  useEffect(() => {
    if (isHovering) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isHovering, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const navigateTo = (url) => {
    if (handleNavigation) {
      handleNavigation(url);
    } else {
      window.location.href = url;
    }
  };

  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Background com slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1, ease: 'easeInOut' }}
          className="absolute inset-0"
        >
          <img
            src={slides[currentSlide].image}
            alt="Hero background"
            className="w-full h-full object-cover"
          />
          {/* Overlay gradiente */}
          <div 
            className="absolute inset-0"
            style={{
              background: `
                linear-gradient(90deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.7) 50%, rgba(15, 23, 42, 0.3) 100%),
                linear-gradient(180deg, rgba(15, 23, 42, 0.3) 0%, rgba(15, 23, 42, 0.5) 100%)
              `,
            }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Partículas flutuantes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Linhas decorativas animadas */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full opacity-10">
          <motion.line
            x1="0" y1="30%" x2="100%" y2="30%"
            stroke="url(#gradient1)"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 3, ease: 'easeInOut' }}
          />
          <motion.line
            x1="0" y1="70%" x2="100%" y2="70%"
            stroke="url(#gradient1)"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 3, delay: 0.5, ease: 'easeInOut' }}
          />
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="50%" stopColor="#10b981" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Conteúdo principal */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Lado esquerdo - Texto */}
          <div className="space-y-8">
            {/* Badge animado */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.4)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles size={16} className="text-[#10b981]" />
              </motion.div>
              <span className="text-sm font-medium text-[#10b981]">
                #1 Plataforma para MEI no Brasil
              </span>
              <div className="flex items-center gap-0.5 ml-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={12} className="text-yellow-400 fill-yellow-400" />
                ))}
              </div>
            </motion.div>

            {/* Título com animação */}
            <div className="space-y-2">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -30 }}
                  transition={{ duration: 0.5 }}
                >
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                    {slides[currentSlide].title}
                    <br />
                    <span 
                      className="relative inline-block"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 50%, #10b981 100%)',
                        backgroundSize: '200% 200%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        animation: 'gradientShift 3s ease infinite',
                      }}
                    >
                      {slides[currentSlide].highlight}
                      {/* Underline animado */}
                      <motion.span
                        className="absolute -bottom-2 left-0 h-1.5 rounded-full"
                        style={{
                          background: 'linear-gradient(90deg, #10b981, #06b6d4)',
                        }}
                        initial={{ width: 0 }}
                        animate={{ width: '100%' }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                      />
                    </span>
                  </h1>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Subtítulo */}
            <AnimatePresence mode="wait">
              <motion.p
                key={currentSlide}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg md:text-xl text-gray-300 max-w-lg leading-relaxed"
              >
                {slides[currentSlide].subtitle}
              </motion.p>
            </AnimatePresence>

            {/* Benefits pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap gap-3"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.text}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20"
                >
                  <benefit.icon size={16} className="text-[#10b981]" />
                  <span className="text-sm font-medium text-white">{benefit.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              {/* CTA Primário */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigateTo('https://app.meumei.net/register')}
                className="group relative px-8 py-4 rounded-2xl font-bold text-lg overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 20px 50px rgba(16, 185, 129, 0.4)',
                }}
              >
                {/* Efeito de brilho */}
                <motion.span
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.6 }}
                />
                {/* Partículas no hover */}
                <span className="absolute inset-0 overflow-hidden">
                  {[...Array(6)].map((_, i) => (
                    <motion.span
                      key={i}
                      className="absolute w-1 h-1 bg-white rounded-full opacity-0 group-hover:opacity-100"
                      style={{
                        left: `${20 + i * 12}%`,
                        bottom: '20%',
                      }}
                      animate={{
                        y: [0, -30, 0],
                        opacity: [0, 1, 0],
                      }}
                      transition={{
                        duration: 1,
                        delay: i * 0.1,
                        repeat: Infinity,
                      }}
                    />
                  ))}
                </span>
                <span className="relative z-10 flex items-center justify-center gap-3 text-white">
                  Abrir meu MEI
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight size={22} />
                  </motion.span>
                </span>
              </motion.button>

              {/* CTA Secundário */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigateTo('https://app.meumei.net/login')}
                className="group relative px-8 py-4 rounded-2xl font-bold text-lg overflow-hidden border-2 border-white/30 hover:border-white/60 transition-colors"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <span className="relative z-10 flex items-center justify-center gap-3 text-white">
                  <Play size={20} className="group-hover:scale-110 transition-transform" />
                  Já sou MEI
                </span>
              </motion.button>
            </motion.div>

            {/* Social proof */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="flex items-center gap-4 pt-4"
            >
              {/* Avatares */}
              <div className="flex -space-x-3">
                {[
                  "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741565086/photo-1633332755192-727a05c4013d_1_tc1a0g.jpg",
                  "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741565089/istockphoto-1386479313-612x612_ufob4o.jpg",
                  "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741565086/istockphoto-1171169127-612x612_og5moq.jpg",
                  "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564663/Leonardo_Kino_XL_create_an_image_where_an_older_white_man_is_l_0_1_twvkdo.jpg",
                ].map((src, i) => (
                  <motion.img
                    key={i}
                    src={src}
                    alt={`User ${i + 1}`}
                    className="w-10 h-10 rounded-full border-2 border-slate-900 object-cover"
                    initial={{ scale: 0, x: -20 }}
                    animate={{ scale: 1, x: 0 }}
                    transition={{ delay: 0.9 + i * 0.1 }}
                  />
                ))}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1.3 }}
                  className="w-10 h-10 rounded-full border-2 border-slate-900 bg-[#10b981] flex items-center justify-center text-white text-xs font-bold"
                >
                  +2M
                </motion.div>
              </div>

              <div className="text-white">
                <motion.p 
                  className="font-semibold"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.4 }}
                >
                  +500mil MEIs
                </motion.p>
                <motion.p 
                  className="text-sm text-gray-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5 }}
                >
                  Nascem todos os Dias, não fique para trás.
                </motion.p>
              </div>
            </motion.div>
          </div>

          {/* Lado direito - Card flutuante com stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="hidden lg:block"
          >
            <div className="relative">
              {/* Card principal */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="relative p-8 rounded-3xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3)',
                }}
              >
                {/* Selo RA1000 */}
                <motion.div
                  initial={{ scale: 0, rotate: -20 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 1, type: 'spring' }}
                  className="absolute -top-6 -right-6 w-26 h-26 rounded-2xl flex flex-col items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  <div className="text-[#10b981] font-bold text-xs">SELO</div>
                  <div className="text-[#2C5E50] font-black text-lg">100% MEI Crescimento</div>
                  <div className="text-[#10b981] font-bold text-[10px]">AQUI!</div>
                </motion.div>

                <h3 className="text-white text-xl font-bold mb-6">Sua jornada MEI começa aqui</h3>

                {/* Stats */}
                <div className="space-y-4">
                  {[
                    { label: 'MEIs cadastrados', value: '1K+', icon: Users },
                    { label: 'Taxa de satisfação', value: '98%', icon: TrendingUp },
                    { label: 'Avaliação média', value: '4.8/5', icon: Star },
                  ].map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + index * 0.15 }}
                      className="flex items-center justify-between p-4 rounded-xl bg-white/10"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#10b981]/20 flex items-center justify-center">
                          <stat.icon size={20} className="text-[#10b981]" />
                        </div>
                        <span className="text-white/80">{stat.label}</span>
                      </div>
                      <span className="text-white font-bold text-lg">{stat.value}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Mini CTA */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigateTo('https://app.meumei.net/register')}
                  className="w-full mt-6 py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  }}
                >
                  Começar agora
                  <ArrowRight size={18} />
                </motion.button>
              </motion.div>

              {/* Elementos decorativos flutuantes */}
              <motion.div
                animate={{ 
                  y: [0, -20, 0],
                  rotate: [0, 5, 0],
                }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -bottom-10 -left-10 w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                  boxShadow: '0 15px 40px rgba(16, 185, 129, 0.4)',
                }}
              >
                <CheckCircle size={32} className="text-white" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Controles do slider */}
      <div className="absolute bottom-8 left-0 right-0 z-20">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Navegação */}
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={prevSlide}
              className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ChevronLeft size={24} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={nextSlide}
              className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ChevronRight size={24} />
            </motion.button>
          </div>

          {/* Indicadores com timer */}
          <div className="flex items-center gap-3">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className="relative group"
              >
                <div
                  className={`h-2 rounded-full transition-all duration-500 overflow-hidden ${
                    currentSlide === index
                      ? 'w-16 bg-white/20'
                      : 'w-2 bg-white/40 group-hover:bg-white/60'
                  }`}
                >
                  {/* Barra de progresso do timer */}
                  {currentSlide === index && !isHovering && (
                    <motion.div
                      className="h-full bg-[#10b981] rounded-full"
                      initial={{ width: '0%' }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 6, ease: 'linear' }}
                      key={`progress-${currentSlide}`}
                    />
                  )}
                  {currentSlide === index && isHovering && (
                    <div 
                      className="h-full bg-[#10b981]/60 rounded-full"
                      style={{ width: '100%' }}
                    />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Contador de slides */}
          <div className="text-white/60 font-medium">
            <span className="text-white text-xl">{String(currentSlide + 1).padStart(2, '0')}</span>
            <span className="mx-2">/</span>
            <span>{String(slides.length).padStart(2, '0')}</span>
          </div>
        </div>
      </div>

      {/* Gradient de transição para próxima seção */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, white, transparent)',
        }}
      />

      {/* CSS para animação do gradiente */}
      <style jsx>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;